import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Filter, Clock, CheckCircle, XCircle, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, X
} from 'lucide-react';
import api from '../../lib/api';

interface Order {
  id: string;
  tokenId: string;
  tokenName: string;
  tokenSymbol: string;
  side: 'BUY' | 'SELL';
  type: 'MARKET' | 'LIMIT';
  status: 'PENDING' | 'PARTIAL' | 'FILLED' | 'CANCELLED' | 'EXPIRED';
  quantity: number;
  filledQuantity: number;
  price: number | null;
  avgFillPrice: number | null;
  totalValue: number;
  fee: number;
  createdAt: string;
  updatedAt: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  PENDING: { label: 'Pendente', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  PARTIAL: { label: 'Parcial', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  FILLED: { label: 'Executada', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  CANCELLED: { label: 'Cancelada', color: 'bg-surface-600 text-surface-300', icon: XCircle },
  EXPIRED: { label: 'Expirada', color: 'bg-red-500/20 text-red-400', icon: AlertTriangle },
};

export function OrderHistory() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sideFilter, setSideFilter] = useState('');

  const { data: orders, isLoading } = useQuery({
    queryKey: ['order-history', search, statusFilter, sideFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (sideFilter) params.append('side', sideFilter);
      const res = await api.get(`/tokenization/trading/orders?${params}`);
      return res.data as Order[];
    },
  });

  const cancelOrder = useMutation({
    mutationFn: async (orderId: string) => {
      await api.post(`/tokenization/trading/orders/${orderId}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-history'] });
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/banking/tokens/portfolio')}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Histórico de Ordens</h1>
          <p className="text-surface-400">Acompanhe suas ordens de compra e venda</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por token..."
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
            />
          </div>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Todos os Status</option>
          <option value="PENDING">Pendente</option>
          <option value="PARTIAL">Parcial</option>
          <option value="FILLED">Executada</option>
          <option value="CANCELLED">Cancelada</option>
          <option value="EXPIRED">Expirada</option>
        </select>

        <select
          value={sideFilter}
          onChange={(e) => setSideFilter(e.target.value)}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg"
        >
          <option value="">Compra e Venda</option>
          <option value="BUY">Compra</option>
          <option value="SELL">Venda</option>
        </select>
      </div>

      {/* Orders Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-surface-400">Carregando ordens...</p>
          </div>
        ) : !orders?.length ? (
          <div className="p-8 text-center">
            <Clock className="w-12 h-12 text-surface-600 mx-auto mb-3" />
            <p className="text-surface-400">Nenhuma ordem encontrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Token</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Tipo</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Quantidade</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Preço</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-surface-400">Total</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-surface-400">Data</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {orders.map((order) => {
                const StatusIcon = statusConfig[order.status].icon;
                return (
                  <tr key={order.id} className="hover:bg-surface-800/50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`p-1 rounded ${
                          order.side === 'BUY' ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          {order.side === 'BUY' ? (
                            <ArrowDownRight className="w-4 h-4 text-green-400" />
                          ) : (
                            <ArrowUpRight className="w-4 h-4 text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{order.tokenName}</p>
                          <p className="text-sm text-surface-400">{order.tokenSymbol}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className={order.side === 'BUY' ? 'text-green-400' : 'text-red-400'}>
                          {order.side === 'BUY' ? 'Compra' : 'Venda'}
                        </p>
                        <p className="text-sm text-surface-400">
                          {order.type === 'MARKET' ? 'Mercado' : 'Limitada'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p>{order.filledQuantity.toLocaleString('pt-BR')}</p>
                      {order.filledQuantity !== order.quantity && (
                        <p className="text-sm text-surface-400">
                          de {order.quantity.toLocaleString('pt-BR')}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {order.avgFillPrice ? (
                        <p>{formatCurrency(order.avgFillPrice)}</p>
                      ) : order.price ? (
                        <p className="text-surface-400">{formatCurrency(order.price)}</p>
                      ) : (
                        <p className="text-surface-400">Mercado</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p>{formatCurrency(order.totalValue)}</p>
                      {order.fee > 0 && (
                        <p className="text-sm text-surface-400">Taxa: {formatCurrency(order.fee)}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig[order.status].color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[order.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-surface-400">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {(order.status === 'PENDING' || order.status === 'PARTIAL') && (
                        <button
                          onClick={() => cancelOrder.mutate(order.id)}
                          disabled={cancelOrder.isPending}
                          className="p-2 hover:bg-surface-700 rounded-lg transition-colors text-red-400"
                          title="Cancelar ordem"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default OrderHistory;
